// Focus highlights, the parts over the board (slice 13; design/08 §5,
// decisions 23, 33; design/22 Q4): the composer while pointing — the count
// of squares, the note, whether it also posts to the log, and the words
// that say a note is visible to everyone; the arrows at the edge of the
// screen for every focus off it, each travelling there on a click; and
// the note under the hand.
import { useEffect, useState } from 'react';
import { FOCUS_LIMITS, NOTE_IS_PUBLIC, memberLabel, type Focus, type NamingObject, type TableMember } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { useShell } from '../shell/context';
import { useLook } from '../shell/useLook';
import { boardBus, type BoardViewState, type FocusHover } from './bus';
import { arrowFor, focusRect } from './focusArrows';
import { useStaging } from './sceneMap';
import { focusTool, type FocusDraft } from './focusTool';
import { pressWord } from '../shell/device';
import { BTN } from '../shell/ui';

const btn = BTN;

function useDraft(): FocusDraft {
  const [d, setD] = useState(focusTool.get());
  useEffect(() => focusTool.subscribe(setD), []);
  return d;
}

/** Who set a focus, for its label: you, a character and handle (issue 1023), or a former player (design/24 Q3). */
export function focusOwnerName(f: Pick<Focus, 'ownerId'>, members: TableMember[], meId: string, objects: Record<string, NamingObject> = {}): string {
  return memberLabel(members.find((x) => x.id === f.ownerId), objects, { meId, you: 'you' });
}

export function FocusComposer() {
  const { act, meId } = useTableState();
  const draft = useDraft();
  const [note, setNote] = useState('');
  const [post, setPost] = useState(false);
  const staging = useStaging(); // the log is the players' too: nothing is posted from a scene they are not on
  useEffect(() => { if (!draft.on) { setNote(''); setPost(false); } }, [draft.on]);
  if (!draft.on) return null;
  const n = draft.cells.length;
  return (
    <form className="pointer-events-auto flex max-w-xl flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="focus-composer" aria-label="Point at a place"
      onSubmit={(e) => {
        e.preventDefault();
        if (n === 0) return;
        act({ kind: 'focus.set', focus: { id: crypto.randomUUID(), ownerId: meId, cells: draft.cells, note: note.trim().slice(0, FOCUS_LIMITS.note), posted: post && !staging } }, (reply) => { if (reply.ok) focusTool.stop(); });
      }}>
      <span className="font-medium text-amber-300">Point at a place</span>
      <span data-testid="focus-count">{n === 0 ? `Drag a box, or ${pressWord().toLowerCase()} squares.` : n === 1 ? '1 square' : `${n} squares`}</span>
      <input aria-label="Note" className="w-48 rounded bg-stone-800 px-2 py-0.5" placeholder="You hear scratching…" maxLength={FOCUS_LIMITS.note} value={note} onChange={(e) => setNote(e.target.value)} />
      {!staging && <label className="flex items-center gap-1"><input type="checkbox" aria-label="Also post the note to the roll log" checked={post} onChange={(e) => setPost(e.target.checked)} /> also post to the log</label>}
      <span className="text-stone-400" data-testid="note-public">{NOTE_IS_PUBLIC}</span>
      <button type="submit" className={`${btn} border-amber-500 text-amber-300`} disabled={n === 0}>Set focus</button>
      {n > 0 && <button type="button" className={btn} onClick={() => focusTool.clearCells()}>Clear squares</button>}
      <button type="button" className={btn} onClick={() => focusTool.stop()}>Cancel</button>
    </form>
  );
}

/** An arrow at the edge for every focus off the screen; clicking it travels there (design/08 §5). */
export function FocusArrows() {
  const { store, meId } = useTableState();
  const { table } = useShell();
  const look = useLook();
  const [cam, setCam] = useState<BoardViewState | null>(null);
  useEffect(() => boardBus.onView(setCam), []);
  if (!cam) return null;
  const focuses = Object.values(store.state.focuses ?? {});
  return (
    <>
      {focuses.map((f) => {
        const rect = focusRect(f.cells, store.state.grid, cam.extent);
        const arrow = arrowFor(rect, cam.view, cam.viewport);
        if (!arrow) return null;
        const who = focusOwnerName(f, table.members, meId, store.state.objects);
        const colour = look.identity(table.members.find((m) => m.id === f.ownerId)?.colour ?? '#a8a29e'); // the person's look (issues 1041, 1445)
        return (
          <button key={f.id} type="button" data-testid={`focus-arrow-${f.id}`} aria-label={`Go to ${who === 'you' ? 'your' : `${who}'s`} focus${f.note ? `: ${f.note}` : ''}`} title={f.note || `${who}'s focus`}
            className="pointer-events-auto absolute flex h-8 w-8 items-center justify-center rounded-full border-2 border-stone-950 text-lg font-bold text-stone-950 shadow"
            style={{ left: arrow.x - 16, top: arrow.y - 16, backgroundColor: colour, transform: `rotate(${arrow.angle}deg)` }}
            onClick={() => boardBus.goTo({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 })}>
            ➜
          </button>
        );
      })}
    </>
  );
}

/** The note of the focus under the hand (decision 33): shown whether or not it was posted. */
export function FocusNote() {
  const { store, meId } = useTableState();
  const { table } = useShell();
  const [hover, setHover] = useState<FocusHover | null>(null);
  useEffect(() => boardBus.onHoverFocus(setHover), []);
  const f = hover ? store.state.focuses?.[hover.id] : undefined;
  if (!hover || !f) return null;
  const who = focusOwnerName(f, table.members, meId, store.state.objects);
  return (
    <div role="tooltip" data-testid="focus-note" className="pointer-events-none absolute z-20 max-w-xs vtt-popover px-2 py-1 text-xs shadow" style={{ left: hover.at.x + 14, top: hover.at.y + 14 }}>
      <span className="text-stone-400">{who === 'you' ? 'Your focus' : `${who}'s focus`}</span>{f.note ? `: ${f.note}` : ''}
    </div>
  );
}
